/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  UseMutationResult,
  useMutation,
} from 'react-query';
import cogoToast from 'cogo-toast';

interface UseQueryFetch {
  /**
   * fetchFn: Fetch promise that returns [status, resp]
   */
  fetchFn: (...args: any[]) => Promise<any>;
  /**
   * queryKey: The query key to use for this query.
   */
  queryKey?: string | unknown[];
  /**
   * errorString: The string that will be shown in a toaster when the fetch doesn't resolve
   */
  errorString?: string;
  /**
   * errorCb: Callback function that returns the unresolved value(s)
   */
  errorCb?: (...args: any[]) => void;
  /**
   * successString: The string that will be shown in a toaster when the fetch resolves
   */
  successString?: string;
  /**
   * successCb: Callback function that returns when the fetch resolves
   */
  successCb?: (...args: any[]) => void;
  /**
   * mutate: mutations are typically used to create/update/delete data or perform server side-effects
   */
  mutate?: boolean;
  /**
   * options: useQuery options => https://react-query.tanstack.com/reference/useQuery
   */
  options?: UseQueryOptions;
}

interface UseQuery extends UseQueryFetch {
  mutate?: false;
}

interface UseMutation extends UseQueryFetch {
  mutate?: true;
}

function useQueryFetch<T>(queryOptions: UseQuery): UseQueryResult<T>;
function useQueryFetch(queryOptions: UseMutation): UseMutationResult;
function useQueryFetch(
  queryOptions: UseQueryFetch,
): UseQueryResult | UseMutationResult {
  const {
    fetchFn,
    queryKey,
    errorString,
    errorCb,
    successString,
    successCb,
    mutate = false,
    options,
  } = queryOptions || {};

  if (!mutate) {
    return useQuery({
      queryKey,
      queryFn: async (...args) => {
        const [resp, status] = await fetchFn(...args);
        if (status >= 400) {
          let error = new Error();
          error = { ...error, ...resp };
          throw error;
        }
        return resp;
      },
      onError: (err) => {
        if (errorString) cogoToast.error(errorString);
        if (errorCb) errorCb(err);
      },
      onSuccess: (data) => {
        if (successString) cogoToast.success(successString);
        if (successCb) successCb(data);
      },
      ...options,
    });
  }
  return useMutation(
    queryKey || '',
    async (...args) => {
      const [resp, status] = await fetchFn(...args);
      if (status > 201) {
        let error = new Error();
        error = { ...error, ...resp };
        throw error;
      }
      return resp;
    }, // @ts-ignore
    {
      onError: (err) => {
        if (errorString) cogoToast.error(errorString);
        if (errorCb) errorCb(err);
      },
      onSuccess: (resp) => {
        if (successString) cogoToast.success(successString);
        if (successCb) successCb(resp);
      },
      ...options,
    },
  );
}

export default useQueryFetch;
